import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChefHat, 
  Clock, 
  Users, 
  Award, 
  Heart, 
  Star,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Utensils,
  Shield,
  Leaf,
  Target
} from 'lucide-react';

const AboutUs = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const stats = [
    { icon: Users, value: '10,000+', label: 'Happy Customers', color: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)' },
    { icon: Utensils, value: '500+', label: 'Dishes Served', color: 'linear-gradient(135deg, #f97316 0%, #ef4444 100%)' },
    { icon: Award, value: '4.8', label: 'Average Rating', color: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)' },
    { icon: Calendar, value: '5+', label: 'Years Experience', color: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)' }
  ];

  const values = [
    {
      icon: Heart,
      title: 'Passion for Food',
      description: 'We believe food is not just sustenance but an expression of love, culture, and tradition. Every dish we prepare is crafted with passion and dedication.',
      color: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
    },
    {
      icon: Leaf,
      title: 'Fresh Ingredients',
      description: 'We source only the freshest, highest quality ingredients from trusted suppliers to ensure every meal meets our premium standards.',
      color: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
    },
    {
      icon: Shield,
      title: 'Quality Assurance',
      description: 'Our rigorous quality control processes ensure that every dish meets our high standards before it reaches your table.',
      color: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
    },
    {
      icon: Target,
      title: 'Customer Satisfaction',
      description: 'Your satisfaction is our top priority. We continuously strive to exceed your expectations with exceptional service and delicious food.',
      color: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
    }
  ];

  const team = [
    {
      name: 'Chef Rajesh Kumar',
      role: 'Head Chef & Founder',
      experience: '15+ years',
      specialty: 'North Indian Cuisine',
      image: 'https://images.unsplash.com/photo-1583394838336-acd977736f90?w=300&h=300&fit=crop&crop=face'
    },
    {
      name: 'Chef Priya Sharma',
      role: 'Executive Chef',
      experience: '12+ years',
      specialty: 'South Indian & Continental',
      image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=300&h=300&fit=crop&crop=face'
    },
    {
      name: 'Amit Singh',
      role: 'Operations Manager',
      experience: '8+ years',
      specialty: 'Quality Control & Logistics',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face'
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f8f9fa 100%)',
      color: '#333',
      fontFamily: 'Inter, sans-serif'
    }}>
      {/* Header */}
      <header style={{
        background: 'white',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 50
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              padding: '12px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #f97316 0%, #ef4444 50%, #ec4899 100%)'
            }}>
              <ChefHat size={24} color="white" />
            </div>
            <h1 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#1f2937',
              margin: 0
            }}>B.Tech Bawarchi</h1>
          </div>
          <nav style={{ display: 'flex', gap: '24px' }}>
            <Link to="/" style={{ color: '#6b7280', textDecoration: 'none', fontWeight: '500' }}>Home</Link>
            <Link to="/about" style={{ color: '#f97316', textDecoration: 'none', fontWeight: '600' }}>About</Link>
            <Link to="/contact" style={{ color: '#6b7280', textDecoration: 'none', fontWeight: '500' }}>Contact</Link>
            <Link to="/menu" style={{ color: '#6b7280', textDecoration: 'none', fontWeight: '500' }}>Menu</Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section style={{
        padding: '60px 16px',
        textAlign: 'center',
        background: 'linear-gradient(135deg, rgba(251, 115, 22, 0.05) 0%, rgba(239, 68, 68, 0.05) 100%)'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          transition: 'all 1s ease',
          opacity: isLoaded ? 1 : 0,
          transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
        }}>
          <h2 style={{
            fontSize: '48px',
            fontWeight: '900',
            color: '#1f2937',
            margin: '0 0 16px 0',
            letterSpacing: '-0.025em',
            lineHeight: '1.1'
          }}>
            About{' '}
            <span style={{
              background: 'linear-gradient(135deg, #f97316 0%, #ef4444 50%, #dc2626 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              B.Tech Bawarchi
            </span>
          </h2>
          <p style={{
            fontSize: '20px',
            color: '#6b7280',
            lineHeight: '1.6',
            margin: '0 0 32px 0'
          }}>
            Discover the story behind Jaipur's most beloved authentic Indian cuisine destination, 
            where tradition meets innovation in every delicious bite.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section style={{
        padding: '80px 16px',
        background: 'white'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr',
            gap: '60px',
            alignItems: 'center'
          }}>
            <div style={{
              transition: 'all 1s ease 0.2s',
              opacity: isLoaded ? 1 : 0,
              transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
            }}>
              <h3 style={{
                fontSize: '36px',
                fontWeight: '800',
                color: '#1f2937',
                margin: '0 0 24px 0',
                lineHeight: '1.2'
              }}>
                Our Story
              </h3>
              <p style={{
                fontSize: '18px',
                color: '#6b7280',
                lineHeight: '1.7',
                margin: '0 0 24px 0'
              }}>
                Founded in 2019 by Chef Rajesh Kumar, B.Tech Bawarchi began as a small kitchen 
                with a big dream: to bring authentic Indian flavors to every home in Jaipur. 
                What started as a passion project has grown into one of the city's most 
                trusted food delivery services.
              </p>
              <p style={{
                fontSize: '18px',
                color: '#6b7280',
                lineHeight: '1.7',
                margin: '0 0 24px 0'
              }}>
                Our name "B.Tech Bawarchi" reflects our innovative approach to traditional 
                cooking - combining time-honored recipes with modern techniques and 
                technology to deliver exceptional food experiences.
              </p>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginTop: '32px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#f97316'
                }}>
                  <Star size={20} fill="currentColor" />
                  <span style={{ fontWeight: '600' }}>4.8/5 Rating</span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#10b981'
                }}>
                  <Users size={20} />
                  <span style={{ fontWeight: '600' }}>10,000+ Customers</span>
                </div>
              </div>
            </div>
            <div style={{
              transition: 'all 1s ease 0.4s',
              opacity: isLoaded ? 1 : 0,
              transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #f97316 0%, #ef4444 100%)',
                borderRadius: '20px',
                padding: '40px',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 20px 40px rgba(251, 115, 22, 0.3)'
              }}>
                <ChefHat size={60} color="white" style={{ marginBottom: '20px' }} />
                <h4 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  margin: '0 0 16px 0'
                }}>
                  "Food is Love Made Edible"
                </h4>
                <p style={{
                  fontSize: '16px',
                  opacity: 0.9,
                  lineHeight: '1.6',
                  margin: 0
                }}>
                  - Chef Rajesh Kumar, Founder
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section style={{
        padding: '80px 16px',
        background: 'linear-gradient(135deg, #f9fafb 0%, #ffffff 100%)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '60px',
            transition: 'all 1s ease 0.6s',
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
          }}>
            <h3 style={{
              fontSize: '36px',
              fontWeight: '800',
              color: '#1f2937',
              margin: '0 0 16px 0'
            }}>
              Our Achievements
            </h3>
            <p style={{
              fontSize: '18px',
              color: '#6b7280',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Numbers that speak for our commitment to excellence and customer satisfaction.
            </p>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 768 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: '32px'
          }}>
            {stats.map((stat, index) => (
              <div
                key={index}
                style={{
                  textAlign: 'center',
                  transition: `all 1s ease ${0.8 + index * 0.1}s`,
                  opacity: isLoaded ? 1 : 0,
                  transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
                }}
              >
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px auto',
                  background: stat.color,
                  boxShadow: '0 10px 20px rgba(0, 0, 0, 0.15)'
                }}>
                  <stat.icon size={36} color="white" />
                </div>
                <h4 style={{
                  fontSize: '32px',
                  fontWeight: '900',
                  color: '#1f2937',
                  margin: '0 0 8px 0'
                }}>
                  {stat.value}
                </h4>
                <p style={{
                  fontSize: '16px',
                  color: '#6b7280',
                  fontWeight: '600',
                  margin: 0
                }}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section style={{
        padding: '80px 16px',
        background: 'white'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '60px',
            transition: 'all 1s ease 1s',
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
          }}>
            <h3 style={{
              fontSize: '36px',
              fontWeight: '800',
              color: '#1f2937',
              margin: '0 0 16px 0'
            }}>
              Our Core Values
            </h3>
            <p style={{
              fontSize: '18px',
              color: '#6b7280',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              The principles that guide everything we do, from sourcing ingredients to serving customers.
            </p>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(2, 1fr)',
            gap: '40px'
          }}>
            {values.map((value, index) => (
              <div
                key={index}
                style={{
                  padding: '32px',
                  background: 'white',
                  borderRadius: '16px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  transition: `all 1s ease ${1.2 + index * 0.1}s`,
                  opacity: isLoaded ? 1 : 0,
                  transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
                }}
              >
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px',
                  background: value.color,
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)'
                }}>
                  <value.icon size={28} color="white" />
                </div>
                <h4 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#1f2937',
                  margin: '0 0 16px 0'
                }}>
                  {value.title}
                </h4>
                <p style={{
                  fontSize: '16px',
                  color: '#6b7280',
                  lineHeight: '1.6',
                  margin: 0
                }}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team */}
      <section style={{
        padding: '80px 16px',
        background: 'linear-gradient(135deg, #f9fafb 0%, #ffffff 100%)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '60px',
            transition: 'all 1s ease 1.4s',
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
          }}>
            <h3 style={{
              fontSize: '36px',
              fontWeight: '800',
              color: '#1f2937',
              margin: '0 0 16px 0'
            }}>
              Meet Our Team
            </h3>
            <p style={{
              fontSize: '18px',
              color: '#6b7280',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              The passionate individuals behind B.Tech Bawarchi's success story.
            </p>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(3, 1fr)',
            gap: '40px'
          }}>
            {team.map((member, index) => (
              <div
                key={index}
                style={{
                  textAlign: 'center',
                  background: 'white',
                  borderRadius: '20px',
                  padding: '32px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                  transition: `all 1s ease ${1.6 + index * 0.1}s`,
                  opacity: isLoaded ? 1 : 0,
                  transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
                }}
              >
                <div style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  margin: '0 auto 24px auto',
                  background: `url(${member.image}) center/cover`,
                  border: '4px solid white',
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)'
                }}></div>
                <h4 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#1f2937',
                  margin: '0 0 8px 0'
                }}>
                  {member.name}
                </h4>
                <p style={{
                  fontSize: '16px',
                  color: '#f97316',
                  fontWeight: '600',
                  margin: '0 0 8px 0'
                }}>
                  {member.role}
                </p>
                <p style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  margin: '0 0 8px 0'
                }}>
                  {member.experience} Experience
                </p>
                <p style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  margin: 0
                }}>
                  Specializes in {member.specialty}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section style={{
        padding: '80px 16px',
        background: 'linear-gradient(135deg, #f97316 0%, #ef4444 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          transition: 'all 1s ease 1.8s',
          opacity: isLoaded ? 1 : 0,
          transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
        }}>
          <h3 style={{
            fontSize: '36px',
            fontWeight: '800',
            margin: '0 0 16px 0'
          }}>
            Ready to Experience Our Food?
          </h3>
          <p style={{
            fontSize: '18px',
            opacity: 0.9,
            margin: '0 0 32px 0',
            lineHeight: '1.6'
          }}>
            Join thousands of satisfied customers who trust B.Tech Bawarchi for their daily meals.
          </p>
          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <a
              href="/order"
              style={{
                background: 'white',
                color: '#f97316',
                padding: '16px 32px',
                borderRadius: '12px',
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '16px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Order Now
            </a>
            <a
              href="/contact"
              style={{
                background: 'transparent',
                color: 'white',
                padding: '16px 32px',
                borderRadius: '12px',
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '16px',
                border: '2px solid white',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#f97316';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'white';
              }}
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        background: '#1f2937',
        color: 'white',
        padding: '48px 16px 24px 16px'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: '24px'
          }}>
            <div style={{
              padding: '8px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #f97316 0%, #ef4444 50%, #ec4899 100%)'
            }}>
              <ChefHat size={24} color="white" />
            </div>
            <h4 style={{
              fontSize: '20px',
              fontWeight: '700',
              margin: 0
            }}>B.Tech Bawarchi</h4>
          </div>
          <p style={{
            color: '#9ca3af',
            marginBottom: '24px',
            lineHeight: '1.6',
            fontSize: '14px'
          }}>
            Serving authentic Indian cuisine with love and tradition since 2019.
          </p>
          <div style={{
            borderTop: '1px solid #374151',
            paddingTop: '16px',
            color: '#9ca3af',
            fontSize: '12px'
          }}>
            <p style={{ margin: 0 }}>&copy; 2024 B.Tech Bawarchi. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutUs;
